import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { vehiclesAPI, vehicleTypesAPI } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Search,
  Truck,
  Edit,
  Trash2,
  Users
} from "lucide-react";
import { cn } from "../lib/utils";

// Fallback vehicle types
const fallbackVehicleTypes = {
  sedan: "Sedán",
  suv: "SUV",
  van: "Van",
  sprinter: "Sprinter"
};

export default function Unidades() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [vehicleTypes, setVehicleTypes] = useState(fallbackVehicleTypes);
  const [vehicleTypesList, setVehicleTypesList] = useState([]); // Full list for select
  const [formData, setFormData] = useState({
    name: "",
    plate: "",
    vehicle_type: "sedan",
    capacity: 4,
    insurance_policy: "",
    insurance_expiry: "",
    status: "activo",
    notes: ""
  });

  useEffect(() => {
    fetchVehicles();
    fetchVehicleTypes();
  }, []);

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleTypesAPI.getAll();
      if (response.data && response.data.length > 0) {
        const typesMap = {};
        const activeTypes = response.data.filter(t => t.is_active !== false);
        activeTypes.forEach(t => {
          typesMap[t.code] = t.name;
        });
        setVehicleTypes(typesMap);
        setVehicleTypesList(activeTypes); // Store full list for select
        
        // Set default to first active type if current default doesn't exist
        if (activeTypes.length > 0 && !activeTypes.find(t => t.code === formData.vehicle_type)) {
          setFormData(prev => ({ ...prev, vehicle_type: activeTypes[0].code }));
        }
      }
    } catch (error) {
      console.warn("Using fallback vehicle types");
      // Create fallback list
      setVehicleTypesList([
        { code: "sedan", name: "Sedán", capacity: 4 },
        { code: "suv", name: "SUV", capacity: 7 },
        { code: "van", name: "Van", capacity: 12 },
        { code: "sprinter", name: "Sprinter", capacity: 18 }
      ]);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await vehiclesAPI.getAll();
      setVehicles(response.data);
    } catch (error) {
      toast.error("Error al cargar unidades");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await vehiclesAPI.update(editingVehicle.id, formData);
        toast.success("Unidad actualizada");
      } else {
        await vehiclesAPI.create(formData);
        toast.success("Unidad creada");
      }
      setDialogOpen(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name,
      plate: vehicle.plate,
      vehicle_type: vehicle.vehicle_type,
      capacity: vehicle.capacity,
      insurance_policy: vehicle.insurance_policy || "",
      insurance_expiry: vehicle.insurance_expiry || "",
      status: vehicle.status,
      notes: vehicle.notes || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Desactivar esta unidad?")) return;
    try {
      await vehiclesAPI.delete(id);
      toast.success("Unidad desactivada");
      fetchVehicles();
    } catch (error) {
      toast.error("Error al desactivar");
    }
  };

  const resetForm = () => {
    setEditingVehicle(null);
    setFormData({
      name: "",
      plate: "",
      vehicle_type: "sedan",
      capacity: 4,
      insurance_policy: "",
      insurance_expiry: "",
      status: "activo",
      notes: ""
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const filteredVehicles = vehicles.filter(v => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        v.name?.toLowerCase().includes(search) ||
        v.plate?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="unidades-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Unidades</h1>
            <p className="text-slate-500 mt-1">Gestiona la flota de vehículos</p>
          </div>
          <Button onClick={openNewDialog} className="gap-2" data-testid="new-vehicle-btn">
            <Plus className="w-4 h-4" />
            Nueva Unidad
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o placa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : filteredVehicles.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay unidades</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Placas</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Capacidad</TableHead>
                    <TableHead>Póliza</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVehicles.map((vehicle) => (
                    <TableRow key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Truck className="w-5 h-5 text-slate-600" />
                          </div>
                          <span className="font-medium">{vehicle.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{vehicle.plate}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{vehicleTypes[vehicle.vehicle_type]}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-slate-400" />
                          {vehicle.capacity}
                        </div>
                      </TableCell>
                      <TableCell>
                        {vehicle.insurance_policy ? (
                          <span className="text-sm">{vehicle.insurance_policy}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          vehicle.status === "activo" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        )}>
                          {vehicle.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(vehicle)}
                            data-testid={`edit-vehicle-${vehicle.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(vehicle.id)}
                            data-testid={`delete-vehicle-${vehicle.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingVehicle ? "Editar Unidad" : "Nueva Unidad"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Suburban 2024"
                    required
                    data-testid="vehicle-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plate">Placas *</Label>
                  <Input
                    id="plate"
                    value={formData.plate}
                    onChange={(e) => setFormData(prev => ({ ...prev, plate: e.target.value.toUpperCase() }))}
                    placeholder="ABC-123"
                    required
                    data-testid="vehicle-plate-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vehicle_type">Tipo *</Label>
                  <Select
                    value={formData.vehicle_type}
                    onValueChange={(value) => {
                      const selectedType = vehicleTypesList.find(t => t.code === value);
                      setFormData(prev => ({ 
                        ...prev, 
                        vehicle_type: value,
                        // Auto-fill capacity from type if available
                        capacity: selectedType?.capacity || prev.capacity
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="vehicle-type-select">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypesList.length > 0 ? (
                        vehicleTypesList.map(type => (
                          <SelectItem key={type.code} value={type.code}>
                            {type.name} {type.capacity ? `(${type.capacity} pax)` : ''}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="sedan">Sedán</SelectItem>
                          <SelectItem value="suv">SUV</SelectItem>
                          <SelectItem value="van">Van</SelectItem>
                          <SelectItem value="sprinter">Sprinter</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capacity">Capacidad *</Label>
                  <Input
                    id="capacity"
                    type="number"
                    min="1"
                    value={formData.capacity}
                    onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                    required
                    data-testid="vehicle-capacity-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="insurance_policy">Póliza de Seguro</Label>
                  <Input
                    id="insurance_policy"
                    value={formData.insurance_policy}
                    onChange={(e) => setFormData(prev => ({ ...prev, insurance_policy: e.target.value }))}
                    placeholder="POL-001"
                    data-testid="vehicle-insurance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insurance_expiry">Vencimiento</Label>
                  <Input
                    id="insurance_expiry"
                    type="date"
                    value={formData.insurance_expiry}
                    onChange={(e) => setFormData(prev => ({ ...prev, insurance_expiry: e.target.value }))}
                    data-testid="vehicle-expiry-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger data-testid="vehicle-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                    <SelectItem value="mantenimiento">En Mantenimiento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-vehicle-btn">
                  {editingVehicle ? "Actualizar" : "Crear"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
